import { useSyncExternalStore } from 'react';
import {
  createDrcProject, deleteDrcChat, deleteDrcProject, listDrcChats, listDrcProjects, moveDrcChat, renameDrcChat,
  renameDrcProject, starDrcChat, type DrcChatSummary, type DrcProject,
} from './drc';

/**
 * Ask's chats and projects, shared by the list and the open chat's title bar (Gary, 18 Oct: Ask as Claude,
 * with Starred and Projects). Changes show at once and are saved; a failed save loads the truth again.
 */
interface AskState { chats: DrcChatSummary[] | null; projects: DrcProject[] }

let state: AskState = { chats: null, projects: [] };
let userId: string | null = null;
const listeners = new Set<() => void>();
const set = (next: Partial<AskState>) => { state = { ...state, ...next }; for (const l of listeners) l(); };

export function useAsk(): AskState {
  return useSyncExternalStore((l) => { listeners.add(l); return () => listeners.delete(l); }, () => state);
}

export async function loadAsk(user: string | null, sample?: { chats: DrcChatSummary[]; projects: DrcProject[] }) {
  userId = user;
  if (!user) { set(sample ?? { chats: [], projects: [] }); return; }
  const [chats, projects] = await Promise.all([listDrcChats(user).catch(() => []), listDrcProjects().catch(() => [])]);
  set({ chats, projects });
}

async function save(change: () => Promise<void>) {
  try { await change(); return true; } catch { await loadAsk(userId); return false; }
}

const patchChat = (id: string, patch: Partial<DrcChatSummary>) =>
  set({ chats: (state.chats ?? []).map((c) => (c.id === id ? { ...c, ...patch } : c)) });

export const askActions = {
  star: (id: string, starred: boolean) => { patchChat(id, { starred_at: starred ? new Date().toISOString() : null }); return save(() => starDrcChat(id, starred)); },
  move: (id: string, projectId: string | null) => { patchChat(id, { project_id: projectId }); return save(() => moveDrcChat(id, projectId)); },
  rename: (id: string, title: string) => { patchChat(id, { title }); return save(() => renameDrcChat(id, title)); },
  remove: (id: string) => { set({ chats: (state.chats ?? []).filter((c) => c.id !== id) }); return save(() => deleteDrcChat(id)); },
  createProject: async (name: string): Promise<DrcProject | null> => {
    try {
      const p = await createDrcProject(name);
      set({ projects: [...state.projects, p].sort((a, b) => a.name.localeCompare(b.name)) });
      return p;
    } catch { return null; }
  },
  renameProject: (id: string, name: string) => {
    set({ projects: state.projects.map((p) => (p.id === id ? { ...p, name } : p)) });
    return save(() => renameDrcProject(id, name));
  },
  removeProject: (id: string) => {
    set({ projects: state.projects.filter((p) => p.id !== id), chats: (state.chats ?? []).map((c) => (c.project_id === id ? { ...c, project_id: null } : c)) });
    return save(() => deleteDrcProject(id));
  },
};
